<template>
  <section>
    <section class="text-right">
      <mf-button class="ma-2" outlined :disabled="existSelectedItems" label="Aprovar Selecionados" @click="acceptRequests(null)" />
    </section>

    <v-card class="my-4">
      <v-card-title class="px-6 pb-0">
        <v-row>
          <v-col cols="8">
            <span class="table-card-title">Solicitações de exclusão de dados</span>
            <p class="section-subtitle pt-1">
              São apresentadas aqui todas as solicitações de exclusão de dados realizadas no site de cadastramento. Por implicações legais, recomendamos que
              elas sejam respondidas dentro de um prazo de 15 dias e te informaremos com antecedência quando uma solicitação estiver atingindo essa data. Os
              dados de um usuário que pertencem ao ERP devem ser excluídos exclusivamente no ERP.
            </p>
          </v-col>
          <v-col cols="4">
            <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Buscar pelo CPF/CNPJ ou Nome/Razão social" />
          </v-col>
        </v-row>
      </v-card-title>
      <paginated-data-table
        ref="table"
        :headers="headers"
        :search="search"
        :query="query"
        queryName="usersPendingDeleteDataRequest"
        :queryVariables="queryVariables"
        :selected-items.sync="selectedItems"
        show-select
        item-key="_id"
        @update-options="updateVariables"
      >
        <template v-slot:item="{ item, isSelected, select }">
          <tr>
            <td>
              <v-checkbox :input-value="isSelected" @change="select"></v-checkbox>
            </td>
            <td>
              {{ item.document_no | cpfCnpj }}
            </td>
            <td>
              {{ item.name | hifen }}
            </td>
            <td>
              {{ item.pending_delete_data_request.data | hifen }}
            </td>
            <td>
              {{ item.pending_delete_data_request.requested_at | date({ fmtIn: 'yyyy-MM-dd HH:mm:ss', fmtOut: 'dd/MM/yyyy | HH:mm' }) }}

              <v-tooltip v-if="requestDateWasExpired(item.pending_delete_data_request.requested_at)" top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <mf-icon fill small color="vermelho-l20" class="ml-1">warning</mf-icon>
                  </span>
                </template>
                <span>{{ getAlertRequestDateDaysLimitLabel(item.pending_delete_data_request.requested_at).danger }}</span>
              </v-tooltip>

              <v-tooltip v-else-if="requestDateIsAlmostExpired(item.pending_delete_data_request.requested_at)" top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <mf-icon fill small color="#ffc107" class="ml-1">warning</mf-icon>
                  </span>
                </template>
                <span>{{ getAlertRequestDateDaysLimitLabel(item.pending_delete_data_request.requested_at).warning }}</span>
              </v-tooltip>
            </td>
            <td class="text-right">
              <a class="error--text" @click="openDeclineRequestModal(item)">Reprovar</a>
              <a class="info--text ml-6 mr-1" @click="acceptRequests(item)">Aprovar</a>
            </td>
          </tr>
        </template>
      </paginated-data-table>

      <decline-request-modal
        :status-decline-request-modal.sync="statusDeclineRequestModal"
        :selectedRequest="selectedRequest"
        @updateTable="updateTable"
        @closeDeclineRequestModal="closeDeclineRequestModal"
        @setTotalDeleteDataRequest="setTotalDeleteDataRequest"
      />
    </v-card>
  </section>
</template>

<script>
import { assertArray } from '@/config/helpers.js'
import { QUERY_GET_USERS_PENDING_DELETE_DATA_REQUEST, MUTATION_ACCEPT_USER_DELETE_DATA_REQUEST } from '@/modules/clientes/gerenciamento/graphql'
import { DateTime } from 'luxon'
import DeclineRequestModal from '@/modules/clientes/gerenciamento/views/components/DeclineRequestModal.vue'

export default {
  name: 'UsersDeleteDataRequests',

  components: {
    PaginatedDataTable: () => import('@/components/PaginatedDataTable.vue'),
    DeclineRequestModal,
  },

  data() {
    return {
      search: '',
      selectedItems: [],
      alertRequestDateDaysLimit: {
        max: 15,
        min: 4,
      },
      selectedRequest: {
        name: '',
        document_no: '',
        pending_delete_data_request: {
          data: [],
          requested_at: '',
        },
      },
      statusDeclineRequestModal: false,
      queryVariables: {
        sort: ['pending_delete_data_request.requested_at'],
        sortDirection: ['DESC'],
      },
    }
  },

  computed: {
    existSelectedItems() {
      return !this.selectedItems.length
    },
    query() {
      return QUERY_GET_USERS_PENDING_DELETE_DATA_REQUEST
    },
    headers() {
      return [
        {
          text: 'CPF/CNPJ',
          value: 'document_no',
          width: '20%',
        },
        {
          text: 'Nome/Razão social',
          value: 'name',
          width: '25%',
        },
        {
          text: 'Dado solicitado',
          value: 'pending_delete_data_request.data',
          width: '20%',
        },
        {
          text: 'Data da solicitação',
          value: 'pending_delete_data_request.requested_at',
          width: '20%',
        },
        {
          text: '',
          value: '',
          sortable: false,
          width: '15%',
        },
      ]
    },
  },

  methods: {
    updateVariables(newQueryOptions) {
      const { sortBy, sortDesc } = newQueryOptions

      if (sortBy.length && sortDesc.length) {
        this.queryVariables.sort = sortBy
        this.queryVariables.sortDirection = sortDesc[0] ? ['DESC'] : ['ASC']
      }
    },

    diffDateDaysToCurrentDate(date) {
      const { days = 0 } = DateTime.fromFormat(date, 'yyyy-MM-dd HH:mm:ss').diffNow(['days']).toObject()
      const isNegative = Math.sign(Math.round(days)) === -1
      const daysAbsoluteValue = Math.abs(Math.round(days))

      return {
        isNegative,
        daysAbsoluteValue,
      }
    },

    requestDateWasExpired(date) {
      const quantityDaysIsNegative = this.diffDateDaysToCurrentDate(date).isNegative
      const requestDaysWasExpired = this.diffDateDaysToCurrentDate(date).daysAbsoluteValue >= this.alertRequestDateDaysLimit.max

      return quantityDaysIsNegative && requestDaysWasExpired
    },

    requestDateIsAlmostExpired(date) {
      const quantityDaysIsNegative = this.diffDateDaysToCurrentDate(date).isNegative
      const requestDaysIsAlmostExpired =
        this.diffDateDaysToCurrentDate(date).daysAbsoluteValue >= this.alertRequestDateDaysLimit.min &&
        this.diffDateDaysToCurrentDate(date).daysAbsoluteValue < this.alertRequestDateDaysLimit.max

      return quantityDaysIsNegative && requestDaysIsAlmostExpired
    },

    getAlertRequestDateDaysLimitLabel(date) {
      const remainingDaysBeforeExpire = this.alertRequestDateDaysLimit.max - this.diffDateDaysToCurrentDate(date).daysAbsoluteValue
      const dayOrDaysText = remainingDaysBeforeExpire === 1 ? 'dia' : 'dias'

      return {
        danger: `Essa solicitação está aguardando resposta a mais de ${this.alertRequestDateDaysLimit.max} dias.`,
        warning: `Em ${remainingDaysBeforeExpire} ${dayOrDaysText} essa solicitação poderá causar implicações legais por falta de resposta.`,
      }
    },

    async acceptRequests(user) {
      const users = assertArray(user || this.selectedItems)
      const confirmMessageLabel =
        users.length > 1
          ? `Clicando em aprovar você confirma a deleção de dados de ${this.selectedItems.length} cadastros.`
          : 'Clicando em aprovar você confirma a deleção de dados de 1 cadastro.'
      const successMessageLabel = users.length > 1 ? 'As solicitações foram aprovadas com sucesso' : 'Solicitação aprovada com sucesso'

      try {
        await this.$confirm({
          title: 'Aprovar solicitações',
          message: confirmMessageLabel,
          confirmText: 'Aprovar',
          confirmColor: 'primary',
          cancelText: 'Cancelar',
        })

        const users_id = users.map(({ _id }) => _id)
        const {
          data: { acceptUserDeleteDataRequest },
        } = await this.$apollo.mutate({
          mutation: MUTATION_ACCEPT_USER_DELETE_DATA_REQUEST,
          variables: { users_id },
        })
        const { requestWasModified, records } = acceptUserDeleteDataRequest

        if (requestWasModified) {
          this.updateTable()
          this.setTotalDeleteDataRequest(records)
          this.$snackbar({ message: successMessageLabel, snackbarColor: 'success' })
        } else {
          this.$snackbar({ message: 'Nenhuma solicitação foi aprovada', snackbarColor: 'error' })
        }
      } catch (error) {
        if (error.message) {
          console.error(error)
          this.$snackbar({ message: 'Erro ao tentar enviar dados para o servidor', snackbarColor: 'error' })
        }
      }
    },

    updateTable() {
      this.$refs.table.clearSelectedInternalItems()
      this.$refs.table.waitAndGetData()
      this.$emit('updateUsersDeleteDataRequestsHistoryTable')
      this.$emit('updateQuitClubRequestsHistoryTable')
    },

    clearSelectedRequest() {
      this.selectedRequest = {
        name: '',
        document_no: '',
        pending_delete_data_request: {
          data: [],
          requested_at: '',
        },
      }
    },

    openDeclineRequestModal(request) {
      this.statusDeclineRequestModal = true
      this.selectedRequest = request
    },

    closeDeclineRequestModal() {
      this.statusDeclineRequestModal = false
      this.clearSelectedRequest()
    },

    setTotalDeleteDataRequest(total) {
      this.$emit('setTotalDeleteDataRequest', total)
    },
  },
}
</script>

<style lang="scss" scoped>
.section-subtitle {
  @extend .texto-corrido;
  word-break: break-word;
}
</style>
